import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Typography,
} from "@mui/material";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import {
  departmentService,
  MAX_DEPTH,
  readApiError,
} from "../../services/departmentService";
import { countDescendants, isSelfOrDescendant, walkTree } from "./treeUtils";

const BLUE = {
  50: "#e8f6f7",
  100: "#c2e6ea",
  200: "#c2e6ea",
  600: "#0c8a9b",
  700: "#0a7480",
  900: "#0f172a",
};
const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Reparents a level. A picker rather than drag-and-drop: a six-deep tree in a
// 380px column makes a drop target a pixel-hunt, and the destination here has to
// be *read* — half the candidates are illegal, and a list can say why while a
// drop target can only refuse.
const MoveNodeDialog = ({
  open,
  onClose,
  node,
  roots,
  departmentName,
  onMoved,
}) => {
  const [target, setTarget] = useState("__root__");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    setTarget(node?.parent_id ? String(node.parent_id) : "__root__");
  }, [open, node]);

  // How much of the subtree hangs below the level being moved. Its deepest
  // descendant has to still fit under the cap wherever it lands, so candidates
  // that would push past it are shown as unavailable rather than hidden — a
  // missing option reads as a bug, a disabled one explains itself.
  const subtreeDepth = useMemo(() => {
    if (!node) return 1;
    let deepest = node.level_no || 1;
    walkTree(node.children || [], (child) => {
      deepest = Math.max(deepest, child.level_no || 1);
    });
    return deepest - (node.level_no || 1) + 1;
  }, [node]);

  const candidates = useMemo(() => {
    if (!node) return [];
    const list = [];
    walkTree(roots, (candidate, trail) => {
      if (isSelfOrDescendant(node, candidate.id)) return;
      const wouldBeDepth = (candidate.level_no || trail.length + 1) + 1;
      list.push({
        node: candidate,
        trail,
        // Legal targets only differ by whether the subtree still fits.
        blocked: wouldBeDepth + subtreeDepth - 1 > MAX_DEPTH,
        wouldBeDepth,
      });
    });
    return list;
  }, [roots, node, subtreeDepth]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await departmentService.moveNode(node.id, {
        new_parent_id: target === "__root__" ? null : Number(target),
      });
      onMoved();
      onClose();
    } catch (err) {
      setError(readApiError(err, "Could not move this level."));
    } finally {
      setSaving(false);
    }
  };

  if (!node) return null;

  const below = countDescendants(node);
  const unchanged =
    (node.parent_id ? String(node.parent_id) : "__root__") === target;

  const Row = ({ value, label, sub, blocked, reason }) => {
    const selected = target === value;
    return (
      <Box
        onClick={() => !blocked && setTarget(value)}
        role="radio"
        aria-checked={selected}
        aria-disabled={blocked}
        tabIndex={blocked ? -1 : 0}
        onKeyDown={(event) => {
          if (!blocked && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setTarget(value);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.75,
          borderRadius: "9px",
          cursor: blocked ? "not-allowed" : "pointer",
          opacity: blocked ? 0.5 : 1,
          bgcolor: selected ? BLUE[50] : "transparent",
          "&:hover": { bgcolor: blocked ? "transparent" : BLUE[50] },
        }}
      >
        <Radio size="small" checked={selected} disabled={blocked} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 14,
              fontWeight: selected ? 700 : 500,
              color: "#1e293b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          {(sub || reason) && (
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 11.5,
                color: blocked ? "#b45309" : "#94a3b8",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {blocked ? reason : sub}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
    >
      <DialogTitle
        sx={{ fontFamily: fontFam, fontWeight: 700, color: BLUE[900], pb: 0.5 }}
      >
        Move “{node.name}”
        <Typography
          sx={{ fontFamily: fontFam, fontSize: 13, color: "#64748b", mt: 0.25 }}
        >
          {below
            ? `${below} level(s) beneath it move too. Templates stay attached; jobs keep the department they were opened under.`
            : "Templates stay attached, and people keep every other placement."}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        {error && (
          <Alert severity="error" sx={{ fontFamily: fontFam, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography
          sx={{
            fontFamily: fontFam,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#94a3b8",
            mb: 0.5,
          }}
        >
          New parent
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 320, overflowY: "auto" }}>
          <Row
            value="__root__"
            label={`${departmentName || "Department"} — top level`}
            sub="No parent; sits alongside the other top levels"
            blocked={subtreeDepth > MAX_DEPTH}
            reason={`Subtree is ${subtreeDepth} deep, limit is ${MAX_DEPTH}`}
          />
          {candidates.map(({ node: candidate, trail, blocked, wouldBeDepth }) => (
            <Row
              key={candidate.id}
              value={String(candidate.id)}
              label={candidate.name}
              sub={
                trail.length
                  ? trail.map((item) => item.name).join(" › ")
                  : "Top level"
              }
              blocked={blocked}
              reason={`Would put levels at depth ${
                wouldBeDepth + subtreeDepth - 1
              }; limit is ${MAX_DEPTH}`}
            />
          ))}
        </Box>

        {candidates.length === 0 && (
          <Typography
            sx={{ fontFamily: fontFam, fontSize: 13.5, color: "#64748b", py: 1 }}
          >
            Nowhere else to put it — every other level is inside this one.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ fontFamily: fontFam, color: "#64748b", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={saving || unchanged}
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <NorthWestIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            fontFamily: fontFam,
            textTransform: "none",
            fontWeight: 600,
            bgcolor: BLUE[600],
            "&:hover": { bgcolor: BLUE[700] },
          }}
        >
          {unchanged ? "Already there" : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveNodeDialog;
